// Copyright (c) 2024 by Audere

import { Box } from "@mui/material";
import { createStyleSheet, globalStyles } from "../utils/styles";
import { useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import React, { useEffect } from "react";
import { WarningAlert } from "../components/Alerts";
import { getLabelType } from "../features/label/labelTypes";
import LabelerConversations from "../components/LabelerConversations";
import QCConversations from "../components/QCConversations";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useLabelingSchema } from "../hooks/useSchema";
import {
  setLastUpdatedTasks,
  setQCCorrectLabels,
  setSchemaVersion,
  setTaskStatuses,
} from "../features/slices/labelSlice";
import { useAppDispatch, useAppSelector } from "../features/store";
import MonitorConversations from "../components/MonitorConversations";
import { useLabels } from "../hooks/useLabels";
import { useLabelsForMonitor } from "../hooks/useLabelsForMonitor";

const Dashboard: React.FC = () => {
  const { labelTypeID, conversationUID } = useParams();
  const { isLabeler, isMonitor, isQC } = useCurrentUser();
  const { labelingTypes } = useLabelingSchema();
  const labelType = getLabelType(labelTypeID ?? "", labelingTypes);
  const { labels, refetch: refetchLabels } = useLabels(conversationUID ?? "");
  const { labels: monitorLabels, refetch: refetchMonitorLabels } =
    useLabelsForMonitor(conversationUID ?? "");
  const labeler = useAppSelector((state) => state.label.labeler);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setTaskStatuses(undefined));
    dispatch(setQCCorrectLabels(undefined));
    dispatch(setLastUpdatedTasks({}));
    dispatch(setSchemaVersion(undefined));
  }, [dispatch, conversationUID]);

  useEffect(() => {
    if (
      conversationUID &&
      (isLabeler || ((isQC || isMonitor) && labeler?.labeler_uid))
    ) {
      if (isMonitor) {
        void refetchMonitorLabels();
      } else {
        void refetchLabels();
      }
    }
  }, [
    conversationUID,
    isLabeler,
    isMonitor,
    isQC,
    refetchMonitorLabels,
    refetchLabels,
    labeler?.labeler_uid,
  ]);

  useEffect(() => {
    const schemaVersion = isMonitor
      ? monitorLabels?.labels?.[0]?.labeling_schema_version
      : labels?.labels?.[0]?.labeling_schema_version;
    if (schemaVersion) {
      dispatch(setSchemaVersion(schemaVersion));
    }
  }, [isMonitor, labels, monitorLabels, dispatch]);

  return !labelType ? (
    <WarningAlert message="Invalid label type" />
  ) : (
    <Box sx={globalStyles.pageContainerColumn}>
      {/* Header */}
      <Box flexShrink={0}>
        <PageHeader title={`${labelType.name} Dashboard`} />
      </Box>

      {/* Left and Right Panels*/}
      <Box sx={styles.pageBody}>
        {isLabeler && <LabelerConversations />}
        {isMonitor && <MonitorConversations />}
        {isQC && <QCConversations />}
      </Box>
    </Box>
  );
};

const styles = createStyleSheet({
  pageBody: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
});

export default Dashboard;
